// Sort my searches' browser half (Livewire\Sites\Insights\Activities\SortMySearches; P9 of
// documentation/pr-plans/insights-simplify-plan.md, D106 and D134).
//
// - The ticks of Other queries and of an open topic's searches live here (ss_pick), so ticking a search shows its
//   list's bar at once (4.7): one list holds the selection at a time, and each press sends the ids it acts on, which
//   the server checks against the rows it holds.
// - Topics and search intents' ticks are the activity's own properties
//   ($wire.selected and $wire.allPages, by list), which each row's box writes through wire:model; the helpers
//   below keep the page's tick and the bar's count in step with them at once.
// - A write that moved searches asks for a count (SortMySearches::COUNT_EVENT): once the presses pause
//   (ss_countSoon()), SetupSortFigures reads the figures in a request of its own, the page's one read of them, which
//   the board counts from too; the activity then reads them from the cache (ss_figuresReady()). Sorting ten searches
//   in a row reads the figures once (P95, D211).
// - Each row's ⋮ or ⋯ is an APG menu button (row-menu.blade.php): ss_menuFocus() and ss_menuKey() are its keys, and
//   ss_menuOpensRight() the side it opens to.
//
// Every write goes through the writes module (insights/writes.js), which tells the page's other screens to show
// their out-of-date mark. Every change says itself through the activity's status region, on the page from its first
// paint.

import { coveredTop } from "./insights/covered-top.js";
import { focusOnceShown } from "./insights/focus.js";
import { send as sendWrite } from "./insights/writes.js";

// SortMySearches::STATUS_EVENT: the activity's polite status region.
const STATUS_EVENT = "sort-searches-status";

// SetupSortFigures::READ_EVENT: the page's one read of the figures, asked for once the presses pause.
const READ_EVENT = "setup-sort-read";

// Long enough that a reader ticking several rows hears one count, not one per tick.
const SELECTION_ANNOUNCE_MS = 500;

// How long the presses must pause before the figures count again: a run of moves reads once.
const COUNT_PAUSE_MS = 1500;

// A menu fixed to the window (ss_menuFixedAt()): its gap from its button, and the least room it keeps from the window's
// edges and from what sticks over the page.
const MENU_GAP_PX = 4;
const MENU_MARGIN_PX = 8;

// The writes after which the searches ticked here have gone where they were sent, so the ticks clear.
const MOVING_WRITES = ["confirmChoice", "moveHere", "backToOther", "confirmNewTopic"];

// The left and right of the nearest box round el that cuts off what crosses its edge (overflow other than visible),
// or of the window when none does.
function clipArea(el) {
    for (let node = el.parentElement; node !== null && node !== document.body; node = node.parentElement) {
        if (getComputedStyle(node).overflowX !== "visible") {
            return node.getBoundingClientRect();
        }
    }

    return { left: 0, right: document.documentElement.clientWidth };
}

// A list's ticks as numbers, whatever the browser or the server last wrote.
function idsOf(value) {
    return Array.isArray(value) ? value.map(Number) : [];
}

/**
 * @param {{selectedWords: string, shareWords: string}} config  the bar's ":count selected" and its
 *        "· :percent% of your search impressions". Words only: the root's x-data never changes between renders,
 *        since a changed one gives the root a new data object, and every control drawn before it (a bar's count)
 *        would keep reading the old one. What changes with a read (the site's search impressions but
 *        Branded queries', what a selection's share is of) is read from the root's data-sort-impressions.
 */
export default function InsightsSortSearches(config = {}) {
    return {
        // The searches ticked in Other queries or in an open topic's panel: {list, ids, impressions by id}.
        ss_pick: { list: null, ids: [], impressions: {} },

        // list -> true while its bulk press is out, so a second press waits for the first.
        ss_busy: {},

        ss_announceTimer: null,

        ss_countTimer: null,

        // Who else asked for the count waiting on ss_countTimer (COUNT_EVENT's `from`), or null for this activity alone.
        ss_countFrom: null,

        // The activity's own element, kept from init: a press inside a dialog's own x-data has the dialog as $root.
        ss_rootElement: null,

        init() {
            this.ss_rootElement = this.$root;

            const livewire = typeof window !== "undefined" ? window.Livewire : null;

            if (livewire && typeof livewire.hook === "function") {
                livewire.hook("commit", ({ component, commit, succeed }) => {
                    if (component?.id === this.$wire.$id && (commit.calls || []).some((call) => MOVING_WRITES.includes(call.method))) {
                        succeed(() => this.ss_clearPick());
                    }
                });
            }
        },

        // The Insights toast's Undo (SetupSort::UNDO_EVENT, S43.1): the board puts the change back and says where
        // focus goes, since the toast that held it has gone.
        ss_undo(detail) {
            if (detail && typeof detail.token === "string") {
                sendWrite({ el: this.ss_rootElement, action: "undo_token", payload: { token: detail.token }, livewire: () => this.$wire.undo(detail.token) });
            }
        },

        // A write moved searches (SortMySearches::COUNT_EVENT): once the presses pause, SetupSortFigures reads the
        // figures, the page's one read, which the board counts from too (P95, D211). Who asked (`from`: the board, or
        // Business details) is kept until that read is in, so a run that mixed this activity's own writes with theirs
        // still counts as theirs asked (SortMySearches::recount()).
        ss_countSoon(detail) {
            if (detail && Number(detail.siteId) !== Number(this.$wire.siteId)) {
                return;
            }

            if (typeof detail?.from === "string") {
                this.ss_countFrom = detail.from;
            }

            clearTimeout(this.ss_countTimer);
            this.ss_countTimer = setTimeout(() => window.Livewire?.dispatch(READ_EVENT, { siteId: Number(this.$wire.siteId) }), COUNT_PAUSE_MS);
        },

        // SetupSortFigures' read ended (SetupSortFigures::READY_EVENT): the activity counts from the cache it filled, or
        // says why there are no figures (SortMySearches::countFailed()).
        ss_figuresReady(detail) {
            if (!detail || Number(detail.siteId) !== Number(this.$wire.siteId)) {
                return;
            }

            const from = this.ss_countFrom;

            this.ss_countFrom = null;

            if (detail.state === "ready") {
                this.$wire.recount(from);
            } else {
                this.$wire.countFailed(detail.state);
            }
        },

        // ------------------------------------------------------------------ the searches ticked here

        ss_pickedIds(list = null) {
            return list === null || this.ss_pick.list === list ? [...this.ss_pick.ids] : [];
        },

        ss_pickCount(list = null) {
            return this.ss_pickedIds(list).length;
        },

        ss_isPicked(list, ids) {
            return this.ss_pick.list === list && ids.length > 0 && ids.every((id) => this.ss_pick.ids.includes(id));
        },

        // A row's box: a search of another list starts a new selection, so one bar shows at a time.
        ss_pickRow(list, id, impressions) {
            if (this.ss_pick.list !== list) {
                this.ss_pick = { list, ids: [], impressions: {} };
            }

            if (this.ss_pick.ids.includes(id)) {
                this.ss_pick.ids = this.ss_pick.ids.filter((picked) => picked !== id);
            } else {
                this.ss_pick.ids = [...this.ss_pick.ids, id];
                this.ss_pick.impressions[id] = Number(impressions) || 0;
            }

            if (this.ss_pick.ids.length === 0) {
                this.ss_pick.list = null;
            }

            this.ss_sayPick();
        },

        // The page's box: every search of the page, or none.
        ss_pickPage(list, impressionsById) {
            const ids = Object.keys(impressionsById);

            if (this.ss_isPicked(list, ids)) {
                this.ss_clearPick();
            } else {
                this.ss_pick = { list, ids, impressions: Object.fromEntries(ids.map((id) => [id, Number(impressionsById[id]) || 0])) };
            }

            this.ss_sayPick();
        },

        ss_clearPick() {
            this.ss_pick = { list: null, ids: [], impressions: {} };
        },

        // ":count selected · :percent% of your search impressions", the share once the read is in.
        ss_pickText(list) {
            const count = this.ss_pickCount(list);
            const words = String(config.selectedWords ?? ":count selected").replace(":count", count.toLocaleString());
            const total = Number(this.ss_rootElement?.dataset?.sortImpressions) || 0;

            if (total <= 0) {
                return words;
            }

            const picked = this.ss_pickedIds(list).reduce((sum, id) => sum + (this.ss_pick.impressions[id] || 0), 0);
            const percent = (picked / total) * 100;
            const shown = percent >= 10 || percent === 0 ? Math.round(percent).toString() : percent.toFixed(1);

            return words + String(config.shareWords ?? "").replace(":percent", shown);
        },

        ss_sayPick() {
            clearTimeout(this.ss_announceTimer);
            this.ss_announceTimer = setTimeout(() => this.ss_say(this.ss_pickText(this.ss_pick.list)), SELECTION_ANNOUNCE_MS);
        },

        // ------------------------------------------------------------------ paged topic lists (Ola, 9 Oct)

        ss_selected(list) {
            return idsOf(this.$wire.selected?.[list]);
        },

        ss_isEvery(list) {
            return this.$wire.allPages?.[list] === true;
        },

        // Every row of the list once "Select all" took it past the page, else the rows ticked.
        ss_count(list, total) {
            return this.ss_isEvery(list) ? total : this.ss_selected(list).length;
        },

        ss_isPageSelected(list, ids) {
            const selected = this.ss_selected(list);

            return ids.length > 0 && ids.every((id) => selected.includes(Number(id)));
        },

        // Some of the page's rows ticked, not all: the page's tick shows half ticked (its indeterminate state).
        ss_isPagePartly(list, ids) {
            const selected = this.ss_selected(list);

            return ids.some((id) => selected.includes(Number(id))) && !this.ss_isPageSelected(list, ids);
        },

        // Gmail's offer: the whole page is ticked and the list holds more than the page.
        ss_canSelectEvery(list, ids, total) {
            return !this.ss_isEvery(list) && total > ids.length && this.ss_isPageSelected(list, ids);
        },

        ss_selectionText(list, total, words) {
            return this.ss_isEvery(list) ? words.all.replace(":count", total) : words.selected.replace(":count", this.ss_count(list, total));
        },

        // The page's tick: every row of the page, or none. Never every row of the list: that is the bar's own offer.
        ss_togglePage(list, ids) {
            const isAll = this.ss_isPageSelected(list, ids);

            this.$wire.allPages[list] = false;
            this.$wire.selected[list] = isAll ? [] : ids.map(Number);
            this.ss_saySelection(list);
        },

        // A row's own box ticked or cleared (its wire:model wrote the tick).
        ss_rowToggled(list) {
            this.$wire.allPages[list] = false;
            this.ss_saySelection(list);
        },

        // "Select all N": the link leaves the bar, so focus goes to the bar's count, which now says all.
        ss_selectEvery(list) {
            this.$wire.allPages[list] = true;
            this.$nextTick(() => focusOnceShown(document.getElementById(`sort-searches-${list}-selected`)));
        },

        // Clear selection hides the bar that held it, so focus goes to the line above the list.
        ss_clearSelection(list) {
            this.$wire.allPages[list] = false;
            this.$wire.selected[list] = [];
            this.ss_saySelection(list);
            this.$nextTick(() => focusOnceShown(document.getElementById(`sort-searches-${list}-counts`)));
        },

        ss_isBusy(list) {
            return this.ss_busy[list] === true;
        },

        // The bar's press: a write (a search intent) goes through the writes module; a press that is no write only
        // calls its method. Focus goes to the list's heading, since the bar leaves with the ticks.
        async ss_bulk(list, method, arg, isWrite = true) {
            if (this.ss_isBusy(list) || (!this.ss_isEvery(list) && this.ss_selected(list).length === 0)) {
                return;
            }

            if (!isWrite) {
                this.$wire[method](arg);

                return;
            }

            this.ss_busy[list] = true;

            try {
                await sendWrite({ el: this.ss_rootElement, action: "bulk", payload: { list, method, arg }, livewire: () => this.$wire[method](arg) });
            } finally {
                this.ss_busy[list] = false;
            }
        },

        // Previous or Next: the activity draws the page asked for (pageTo()), and the line above or under the list is
        // said, so a screen reader hears where it landed. Focus stays on the button, which stays focusable at either
        // end of the list and then does nothing. Other queries' ticks are of the page they were made on, so they go.
        async ss_page(list, page, button) {
            if (button?.getAttribute("aria-disabled") === "true") {
                return;
            }

            if (this.ss_pick.list === list) {
                this.ss_clearPick();
            }

            await this.$wire.pageTo(list, page);

            this.$nextTick(() => {
                const counts = document.getElementById(`sort-searches-${list}-counts`);

                if (counts) {
                    this.ss_say(counts.textContent.trim());
                }
            });
        },

        // The bar's count, once the ticking has paused: its own words, read from the bar (x-text).
        ss_saySelection(list) {
            clearTimeout(this.ss_announceTimer);
            this.ss_announceTimer = setTimeout(() => {
                const count = document.getElementById(`sort-searches-${list}-selected`);

                if (count) {
                    this.ss_say(count.textContent.trim());
                }
            }, SELECTION_ANNOUNCE_MS);
        },

        // ------------------------------------------------------------------ a row's menu (APG menu button)

        // Every item, an unavailable one too (aria-disabled): the APG keeps a disabled menu item focusable, so a keyboard
        // reaches its words saying why (the Group and sort board's Back to Not sorted yet, D178); pressing it does nothing.
        ss_menuItems(list) {
            return Array.from(list?.querySelectorAll('[role="menuitem"]') ?? []);
        },

        // Focus moves into the menu once it shows: its first item, or its last for Up.
        ss_menuFocus(list, index) {
            this.$nextTick(() => {
                const items = this.ss_menuItems(list);

                focusOnceShown(index < 0 ? items.at(-1) : items[index]);
            });
        },

        // Whether a menu opens rightward from its button, read once it shows, so its own width counts: only when opening
        // leftward, as every row's does, would carry it past the edge of the area round it, which cuts off what crosses
        // it (the page's own area beside the sidebar), and rightward has more room. The Group and sort board's first chip
        // under Topics with no search intent sits about 175 px from that edge, and a menu is at least 240 px wide.
        ss_menuOpensRight(button, list) {
            const area = clipArea(list);
            const at = button.getBoundingClientRect();
            const width = list.getBoundingClientRect().width;

            return width > at.right - area.left && area.right - at.left > at.right - area.left;
        },

        // Where a menu drawn fixed to the window sits (row-menu.blade.php's $fixed: a chip's, P75), once it shows, so its own
        // size counts: under its button, or over it where the window has no room below; from the button's left edge
        // rightward when leftward would cross the window's left side, else ending at the button's right edge; and held
        // inside the window's sides either way. A menu that fits on neither side takes the roomier one and scrolls inside
        // itself there, so no item sits past the window's edge: a fixed menu never moves with the page, so no scroll of
        // the page could bring that item back (a chip's ⋯ of a dozen items in a 720px window left See in Stats out of
        // reach). The height counted is the menu's whole, every item shown, which a menu placed shorter still has. The
        // room above starts under what sticks over the page (coveredTop(): the Insights header, and the board's head
        // while it sticks), not at the window's top, which put a chip's ⋯ near the top under the header (P89).
        ss_menuFixedAt(button, list) {
            const at = button.getBoundingClientRect();
            const width = list.offsetWidth;
            const height = list.scrollHeight + list.offsetHeight - list.clientHeight;
            const room = document.documentElement.clientWidth;
            const left = at.right - width < MENU_MARGIN_PX ? at.left : at.right - width;
            const below = window.innerHeight - MENU_MARGIN_PX - (at.bottom + MENU_GAP_PX);
            const above = at.top - MENU_GAP_PX - MENU_MARGIN_PX - coveredTop();
            const isAbove = height > below && (height <= above || above > below);
            const shown = Math.min(height, Math.max(0, isAbove ? above : below));
            const isCut = shown < height;

            return {
                left: `${Math.max(MENU_MARGIN_PX, Math.min(left, room - width - MENU_MARGIN_PX))}px`,
                top: `${isAbove ? at.top - MENU_GAP_PX - shown : at.bottom + MENU_GAP_PX}px`,
                maxHeight: isCut ? `${shown}px` : null,
                overflowY: isCut ? "auto" : null,
            };
        },

        // Up and Down step through the items and wrap; Home and End go to the ends. Escape and Tab are the menu's own.
        ss_menuKey(event, list) {
            const items = this.ss_menuItems(list);
            const at = items.indexOf(document.activeElement);
            const step = { ArrowDown: 1, ArrowUp: -1 }[event.key];

            if (items.length === 0) {
                return;
            }

            if (step !== undefined) {
                event.preventDefault();
                items[(at + step + items.length) % items.length].focus();
            } else if (event.key === "Home" || event.key === "End") {
                event.preventDefault();
                (event.key === "Home" ? items[0] : items.at(-1)).focus();
            }
        },

        ss_say(message) {
            window.dispatchEvent(new CustomEvent(STATUS_EVENT, { detail: { message } }));
        },
    };
}
